import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { Chart, ChartArea, ChartCategoryAxis, ChartCategoryAxisItem, ChartLegend, ChartSeries, ChartSeriesItem, ChartValueAxis, ChartValueAxisItem, SeriesHoverEvent } from "@progress/kendo-react-charts";
import { useLocalization } from "@progress/kendo-react-intl";
import { Loader } from "@progress/kendo-react-indicators";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { AccessToken } from "../../config/AccessToken";
import { KpiHoverTooltip } from "./KpiHoverTooltip";
import { parseAnyDateTime } from "./dashboardUtils";

type NearmissDay = {
  key: string;
  label: string;
  total: number;
  alarms: number;
  prealarms: number;
};

const DAYS_WINDOW = 7;
const NEARMISS_STATUS_PREALARM = 1;
const NEARMISS_STATUS_ALARM = 2;

const COLOR_ALARM = '#dc2626';
const COLOR_PREALARM = '#f59e0b';

const kpiLabelStyle = { fontSize: 'var(--fs-small)', fontWeight: 700, color: '#6b7280', letterSpacing: '0.03em', textTransform: 'uppercase' as const };

const toDayKey = (date: Date) => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

const buildEmptyWeek = (languageId: string | null): NearmissDay[] => {
  const days: NearmissDay[] = [];

  for (let i = DAYS_WINDOW - 1; i >= 0; i--) {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - i);

    const label = date
      .toLocaleDateString(languageId === 'it-IT' ? 'it-IT' : 'en-US', { day: '2-digit', month: 'short' })
      .replace('.', '');

    days.push({
      key: toDayKey(date),
      label: label.charAt(0).toUpperCase() + label.slice(1),
      total: 0,
      alarms: 0,
      prealarms: 0
    });
  }

  return days;
};

export const NearmissKpiCard = () => {
  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const [instance, accessTokenRequest] = AccessToken();
  const localizationService = useLocalization();
  const languageId = localStorage.getItem('languageId');

  const [days, setDays] = useState<NearmissDay[]>(() => buildEmptyWeek(languageId));
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [hover, setHover] = useState<{ x: number; y: number; day: NearmissDay } | null>(null);

  const t = (key: string, fallback: string) => localizationService.toLanguageString(`dashboard.widget.nearmiss.${key}`, fallback);

  const labels = {
    subtitle: t('subtitle', 'Nearmiss raccolti (ultima settimana)'),
    total: t('total', 'Totale'),
    alarms: t('alarms', 'Allarmi'),
    prealarms: t('prealarms', 'Preallarmi'),
    error: localizationService.toLanguageString('configuration.analysis.heatmap.nearmiss_error', 'Errore durante il caricamento dei nearmiss')
  };

  useEffect(() => {
    const fetchNearmisses = async () => {
      setLoading(true);
      setHasError(false);

      const commonOperations = async (accessToken: any) => {
        const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
        const week = buildEmptyWeek(languageId);

        try {
          const devicesRes = await axios.get(`${SERVER_URL}/device/list`, { headers });
          const macs: string[] = (devicesRes?.data?.data ?? [])
            .map((device: any) => device?.mac)
            .filter((mac: any) => typeof mac === 'string' && mac.length > 0);

          if (macs.length > 0) {
            const startDate = new Date();
            startDate.setHours(0, 0, 0, 0);
            startDate.setDate(startDate.getDate() - (DAYS_WINDOW - 1));

            const response = await axios.post(
              `${SERVER_URL}/nearmiss`,
              { startDate: startDate.toISOString(), endDate: new Date().toISOString(), macs },
              { headers }
            );
            const payload = response?.data?.data;
            // Accept both NearMissesDto ({ mapId, nearMisses }) and a bare NearMissDto[].
            const rawNearmisses: any[] = Array.isArray(payload) ? payload : (payload?.nearMisses ?? []);

            const daysByKey = new Map(week.map((day) => [day.key, day]));
            rawNearmisses.forEach((nearmiss) => {
              const date = parseAnyDateTime(nearmiss?.datetime);
              const day = date ? daysByKey.get(toDayKey(date)) : undefined;
              if (!day) {
                return;
              }
              day.total++;
              const status = Number(nearmiss?.status);
              if (status === NEARMISS_STATUS_ALARM) {
                day.alarms++;
              } else if (status === NEARMISS_STATUS_PREALARM) {
                day.prealarms++;
              }
            });
          }

          setDays(week);
        } catch {
          setDays(week);
          setHasError(true);
        } finally {
          setLoading(false);
        }
      };

      if (localStorage.getItem('accessToken') != null) {
        await commonOperations(localStorage.getItem('accessToken'));
      } else {
        await instance.initialize();
        await instance.acquireTokenSilent(accessTokenRequest)
          .then((accessTokenResponse: any) => commonOperations(accessTokenResponse.accessToken))
          .catch((error: any) => {
            if (error instanceof InteractionRequiredAuthError) {
              instance.acquireTokenRedirect(accessTokenRequest);
            }
            setLoading(false);
          });
      }
    };

    void fetchNearmisses();
  }, []);

  const weekTotal = useMemo(() => days.reduce((sum, day) => sum + day.total, 0), [days]);
  const maxTotal = useMemo(() => Math.max(1, ...days.map((day) => day.total)), [days]);
  const categories = useMemo(() => days.map((day) => day.label), [days]);

  const handleSeriesHover = (event: SeriesHoverEvent) => {
    const day = days.find((item) => item.label === event?.category);
    const nativeEvent = event?.nativeEvent;
    if (day) {
      setHover({ x: nativeEvent?.clientX ?? 0, y: nativeEvent?.clientY ?? 0, day });
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Loader size="small" type="infinite-spinner" themeColor="primary" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', color: '#1f2937' }} onMouseLeave={() => setHover(null)}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '12px', marginBottom: '4px' }}>
        <span style={kpiLabelStyle}>{labels.subtitle}</span>
        <div style={{ textAlign: 'right' }}>
          <span style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1f2937' }}>{weekTotal}</span>
          <div style={kpiLabelStyle}>{labels.total}</div>
        </div>
      </div>

      {hasError && (
        <div style={{ fontSize: '0.8125rem', color: '#b42318', marginBottom: '4px' }}>{labels.error}</div>
      )}

      <div style={{ flex: 1, minHeight: '150px' }}>
        <Chart style={{ height: '100%', width: '100%' }} onSeriesHover={handleSeriesHover} transitions={false}>
          <ChartArea background="transparent" />
          <ChartLegend position="bottom" orientation="horizontal" labels={{ font: '0.8125rem sans-serif', color: '#4b5563' }} />
          <ChartCategoryAxis>
            <ChartCategoryAxisItem
              categories={categories}
              labels={{ font: '0.75rem sans-serif', color: '#4b5563' }}
              line={{ color: '#d1d5db' }}
            />
          </ChartCategoryAxis>
          <ChartValueAxis>
            <ChartValueAxisItem
              min={0}
              max={Math.ceil(maxTotal / 3) * 3}
              labels={{ font: '0.75rem sans-serif', color: '#4b5563' }}
              line={{ color: '#d1d5db' }}
              majorGridLines={{ color: '#e5e7eb' }}
            />
          </ChartValueAxis>
          <ChartSeries>
            <ChartSeriesItem
              type="column"
              stack={true}
              name={labels.alarms}
              data={days.map((day) => day.alarms)}
              color={COLOR_ALARM}
              tooltip={{ visible: false }}
            />
            <ChartSeriesItem
              type="column"
              stack={true}
              name={labels.prealarms}
              data={days.map((day) => day.prealarms)}
              color={COLOR_PREALARM}
              tooltip={{ visible: false }}
            />
          </ChartSeries>
        </Chart>
      </div>

      {hover && (
        <KpiHoverTooltip
          x={hover.x}
          y={hover.y}
          title={`${hover.day.label} · ${labels.total}: ${hover.day.total}`}
          rows={[
            { label: labels.alarms, value: hover.day.alarms },
            { label: labels.prealarms, value: hover.day.prealarms }
          ]}
        />
      )}
    </div>
  );
};
